import { IPublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest } from '../config/msalConfig';
import { fetchRuntimeConfig } from '../utils/config';

let msalInstance: IPublicClientApplication | null = null;

/**
 * Hands the initialised MSAL instance to the API layer so outgoing requests
 * can carry the signed-in user's token. Called once from the MSAL wrapper.
 */
export const registerMsalInstance = (instance: IPublicClientApplication): void => {
  msalInstance = instance;
};

/**
 * Returns the bearer token for calls to our own backend.
 *
 * When the app registration exposes an API scope (REACT_APP_AZURE_API_SCOPE)
 * this is a proper access token for that scope. Until then it is the ID token,
 * which the server validates against the same tenant and client ID.
 */
export const getApiToken = async (): Promise<string> => {
  if (!msalInstance) {
    throw new Error('Authentication is not initialised');
  }

  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new Error('No signed-in account');
  }

  const { REACT_APP_AZURE_API_SCOPE: apiScope } = await fetchRuntimeConfig();
  const request = {
    account,
    scopes: apiScope ? [apiScope] : loginRequest.scopes,
  };

  try {
    const result = await msalInstance.acquireTokenSilent(request);
    return apiScope ? result.accessToken : result.idToken;
  } catch (error) {
    // Silent renewal fails once the refresh token needs the user again, e.g.
    // after a password change or a conditional-access prompt.
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect(request);
    }
    throw error;
  }
};
